import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { OnboardingModulesTypes } from '@frontend/api-onboarding-modules';
import { OnboardingTasksV1 } from '@frontend/api-onboarding-tasks';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Modal, ModalControlModalProps, Text, useAlert } from '@frontend/design-system';
import { WeaveInitialCallScheduleIframe } from './WeaveInitialCallScheduleIframe';

interface WICScheduleModalProps extends ModalControlModalProps {
  onSuccess?: () => void;
  locationId: string;
  closeBtnTrackingId?: string;
}

export const WICScheduleModal: FC<WICScheduleModalProps> = ({
  onSuccess,
  locationId,
  closeBtnTrackingId,
  ...modalProps
}) => {
  const { t } = useTranslation('productOnboardingTasks');
  const [isCloseButtonVisible, setIsCloseButtonVisible] = useState<boolean>();
  const alert = useAlert();

  const { mutate: markLocationTaskCompleteMutate } = OnboardingTasksV1.Mutations.useMarkLocationTaskCompletedMutation({
    options: {
      onError: () => {
        alert.error(t('An error occurred while scheduling the call.'));
      },
      onSuccess,
    },
  });

  const handleScheduleClose = () => {
    closeBtnTrackingId && pendo.track(closeBtnTrackingId);
    modalProps.onClose();
  };

  const handleAvailabilityLoaded = () => {
    // hide close button once iframe is loaded
    // This is commented because close button is sometimes not available in the iframe
    // setIsCloseButtonVisible(false);
  };

  const handleBookingConfirmed = () => {
    markLocationTaskCompleteMutate({ taskId: OnboardingModulesTypes.TaskIds.ScheduleCallWithOnboarder, locationId });
  };

  useEffect(() => {
    if (modalProps.show) {
      setIsCloseButtonVisible(true);
    }
  }, [modalProps.show]);

  return (
    <Modal {...modalProps} maxWidth={600} css={styles.modal}>
      <Modal.Header
        closeBtnTrackingId={closeBtnTrackingId}
        onClose={isCloseButtonVisible ? modalProps.onClose : undefined}
        css={{ button: { height: 32 } }}
      >
        <div css={styles.header}>
          <Icon name='phone-call' />
          <Text size='large' weight='bold'>
            {t('Schedule With a Weave Onboarder')}
          </Text>
        </div>
      </Modal.Header>
      <Modal.Body>
        <WeaveInitialCallScheduleIframe
          show={modalProps.show}
          locationId={locationId}
          onAvailabilityLoaded={handleAvailabilityLoaded}
          onBookingConfirmed={handleBookingConfirmed}
          onClose={handleScheduleClose}
        />
      </Modal.Body>
    </Modal>
  );
};

const styles = {
  modal: css`
    padding: ${theme.spacing(2, 0)};
    height: 90vh;
    max-height: 800px;
  `,
  header: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(1)};
  `,
};
